/**
 * GroupRequestsList Component
 * Displays pending group requests for moderators/admins to review and approve/reject
 *
 * Features:
 * - Table layout with request details (requester, group name, justification, date)
 * - Approval/rejection actions for each request
 * - Loading and empty states
 * - Auto-refreshes when requests are approved/rejected
 */

import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  Tooltip,
  Alert,
  IconButton,
} from '@mui/material';
import {
  PendingActions as PendingIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { GROUP_REQUESTS_REFRESH_MS, usePendingGroupRequests } from '../../hooks/api/useGroupRequests';
import { useUsers } from '../../hooks/api/useUsers';
import { GroupRequestApprovalActions } from './GroupRequestApprovalActions';
import { GroupRequestDetailsDialog } from './GroupRequestDetailsDialog';
import { formatDistanceToNow } from '../../utils/formatting/dateUtils';
import { GroupRequest, RequestStatus } from '../../api/data-contracts';

interface GroupRequestsListProps {
  /**
   * Optional callback when request is approved or rejected successfully
   */
  onRequestProcessed?: () => void;
}

export const GroupRequestsList: React.FC<GroupRequestsListProps> = ({
  onRequestProcessed,
}) => {
  const { t } = useTranslation();

  // State for detail dialog
  const [selectedRequest, setSelectedRequest] = React.useState<GroupRequest | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = React.useState(false);

  // Fetch pending requests
  const {
    data: requestsResponse,
    isLoading: requestsLoading,
    error: requestsError,
  } = usePendingGroupRequests(
    { page: 1, pageSize: 100 },
    { queryOptions: { refetchInterval: GROUP_REQUESTS_REFRESH_MS } }
  );

  // Fetch users for displaying requester names
  const { data: usersData } = useUsers({ skip: 0, limit: 1000 });
  const allUsers = usersData?.users || [];

  const requests = requestsResponse?.requests || [];

  // Create user lookup map for efficient access
  const userMap = useMemo(() => {
    const map = new Map();
    allUsers.forEach((user) => {
      map.set(user.id, user);
    });
    return map;
  }, [allUsers]);

  // Helper to get user display name
  const getUserDisplayName = (userId: string): string => {
    const user = userMap.get(userId);
    if (!user) {return userId;}
    return user.full_name || user.username || user.email || userId;
  };

  // Helper to get status color
  const getStatusColor = (
    status: RequestStatus
  ): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case RequestStatus.Pending:
        return 'warning';
      case RequestStatus.Approved:
        return 'success';
      case RequestStatus.Rejected:
        return 'error';
      case RequestStatus.Cancelled:
        return 'default';
      default:
        return 'default';
    }
  };

  // Helper to get status icon
  const getStatusIcon = (status: RequestStatus) => {
    switch (status) {
      case RequestStatus.Pending:
        return <PendingIcon fontSize="small" />;
      case RequestStatus.Approved:
        return <ApprovedIcon fontSize="small" />;
      case RequestStatus.Rejected:
        return <RejectedIcon fontSize="small" />;
      default:
        return <PendingIcon fontSize="small" />;
    }
  };

  // Handler to open detail dialog
  const handleViewDetails = (request: GroupRequest) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  // Handler to close detail dialog
  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedRequest(null);
  };

  // Show loading state
  if (requestsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (requestsError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {requestsError instanceof Error
          ? requestsError.message
          : t('groupRequests.approval.errorLoading')}
      </Alert>
    );
  }

  // Show empty state
  if (requests.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <PendingIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          {t('groupRequests.approval.emptyState')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('groupRequests.approval.emptyStateDescription')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Summary */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('groupRequests.approval.showing', {
            count: requests.length,
            total: requestsResponse?.pagination?.total || requests.length,
          })}
        </Typography>
      </Box>

      {/* Requests Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('groupRequests.approval.tableHeaders.requester')}</TableCell>
              <TableCell>{t('groupRequests.approval.tableHeaders.groupName')}</TableCell>
              <TableCell>{t('groupRequests.approval.tableHeaders.justification')}</TableCell>
              <TableCell>{t('groupRequests.approval.tableHeaders.requestedDate')}</TableCell>
              <TableCell>{t('groupRequests.approval.tableHeaders.approvers')}</TableCell>
              <TableCell>{t('groupRequests.approval.tableHeaders.status')}</TableCell>
              <TableCell align="center" sx={{ width: 48 }}></TableCell>
              <TableCell align="right">
                {t('groupRequests.approval.tableHeaders.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request) => (
              <TableRow key={request.id} hover>
                {/* Requester */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {getUserDisplayName(request.user_id)}
                  </Typography>
                </TableCell>

                {/* Group Name */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {request.group_name || t('groupRequests.approval.unknownGroup')}
                  </Typography>
                  {request.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {request.description}
                    </Typography>
                  )}
                </TableCell>

                {/* Justification */}
                <TableCell>
                  <Tooltip title={request.justification || request.reason || ''}>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {request.justification || request.reason || t('groupRequests.approval.noJustification')}
                    </Typography>
                  </Tooltip>
                </TableCell>

                {/* Requested Date */}
                <TableCell>
                  <Typography variant="body2">
                    {request.requested_at
                      ? formatDistanceToNow(new Date(request.requested_at), {
                          addSuffix: true,
                        })
                      : '-'}
                  </Typography>
                </TableCell>

                {/* Approvers */}
                <TableCell>
                  {request.approver_ids && request.approver_ids.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {request.approver_ids.slice(0, 2).map((approverId) => (
                        <Chip
                          key={approverId}
                          label={getUserDisplayName(approverId)}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {request.approver_ids.length > 2 && (
                        <Chip
                          label={`+${request.approver_ids.length - 2}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('groupRequests.approval.noApprovers')}
                    </Typography>
                  )}
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Chip
                    icon={getStatusIcon(request.status || RequestStatus.Pending)}
                    label={t(`groupRequests.status.${request.status || 'pending'}`)}
                    color={getStatusColor(request.status || RequestStatus.Pending)}
                    size="small"
                  />
                </TableCell>

                {/* Details Button */}
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleViewDetails(request)}
                    aria-label={t('groupRequests.myRequests.detailDialog.viewDetails')}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </TableCell>

                {/* Actions */}
                <TableCell align="right">
                  {request.status === RequestStatus.Pending && (
                    <GroupRequestApprovalActions
                      request={request}
                      onSuccess={onRequestProcessed}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog */}
      <GroupRequestDetailsDialog
        open={detailDialogOpen}
        request={selectedRequest}
        onClose={handleCloseDetailDialog}
      />
    </Box>
  );
};
